import { useEffect, useState } from 'react';

export interface ExportJob {
  key: string;
  status: 'running' | 'done' | 'error';
  loaded: number;
  total: number;
  rows: any[];
}

type Progress = (loaded: number, total: number) => void;

const MAX_KEPT_JOBS = 3;

// Module scope, so a job outlives the component that started it.
const jobs = new Map<string, ExportJob>();
const promises = new Map<string, Promise<any[]>>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const getExportJob = (key: string): ExportJob | undefined => jobs.get(key);

/** Re-renders the caller whenever any job changes. */
export const useExportJob = (key: string): ExportJob | undefined => {
  const [, bump] = useState(0);
  useEffect(() => {
    const listener = () => bump((n) => n + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);
  return jobs.get(key);
};

/**
 * Runs the full export once per key and caches it, so downloading again never refetches.
 * The key should include the filters. `onProgress` follows the shared job, even when it was
 * started by an earlier click.
 */
export const runExportJob = (
  key: string,
  fetcher: (onProgress: Progress) => Promise<any[]>,
  onProgress?: Progress,
): Promise<any[]> => {
  const report = () => {
    const job = jobs.get(key);
    if (job) onProgress?.(job.loaded, job.total);
  };
  listeners.add(report);

  let promise = promises.get(key);
  if (!promise) {
    const job: ExportJob = { key, status: 'running', loaded: 0, total: 0, rows: [] };
    jobs.set(key, job);
    // Each finished job holds its rows, so don't keep every filter combination of a long session.
    while (jobs.size > MAX_KEPT_JOBS) {
      const oldest = jobs.keys().next().value;
      if (oldest === undefined) break;
      jobs.delete(oldest);
      promises.delete(oldest);
    }

    promise = fetcher((loaded, total) => {
      job.loaded = loaded;
      job.total = total;
      emit();
    })
      .then((rows) => {
        job.rows = rows;
        job.loaded = rows.length;
        job.total = rows.length;
        job.status = 'done';
        emit();
        return rows;
      })
      .catch((error) => {
        // Drop it so a retry starts fresh.
        jobs.delete(key);
        promises.delete(key);
        emit();
        throw error;
      });
    promises.set(key, promise);
  }
  report();
  return promise.finally(() => listeners.delete(report));
};
